// Extracted from App.js — CreateContract page (form for new / edit work orders)
import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Upload, DollarSign, Trash2, Loader2, Wrench, Download, FileCheck } from "lucide-react";
import { API, formatCurrency } from "../utils/api";
import TipButton from "../components/TipButton";

const CreateContract = () => {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = !!editId;
  const [saving, setSaving] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(isEdit);
  const [previewNum, setPreviewNum] = useState('');
  const [currentFY, setCurrentFY] = useState('');
  const [originalContract, setOriginalContract] = useState(null);
  
  // Reason modal for active edits
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [editReason, setEditReason] = useState('');
  
  // Form state
  const [vendor, setVendor] = useState({ name: '', type: '', registered_office: '', representative_name: '', representative_designation: '', gstin: '', pan: '' });
  const [agreementDate, setAgreementDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [monthlyValue, setMonthlyValue] = useState(0);
  const [gstRate, setGstRate] = useState(18);
  const [billingFrequency, setBillingFrequency] = useState('monthly');
  const [status, setStatus] = useState('draft');
  
  // Unified fields
  const [scopeDescription, setScopeDescription] = useState('');
  const [specialConditions, setSpecialConditions] = useState('');
  const [vendorConditions, setVendorConditions] = useState('');
  const [facilitiesProvided, setFacilitiesProvided] = useState('');
  const [amcType, setAmcType] = useState('Work Order');
  
  // Optional maintenance fields
  const [pmFrequency, setPmFrequency] = useState('');
  const [responseTime, setResponseTime] = useState('');
  const [resolutionTime, setResolutionTime] = useState('');
  
  // PM Activities
  const [pmActivities, setPmActivities] = useState(['']);
  const [breakdownObligations, setBreakdownObligations] = useState('');
  
  // Equipment (optional)
  const [showEquipment, setShowEquipment] = useState(false);
  const [showMaintenance, setShowMaintenance] = useState(false);
  const [equipmentList, setEquipmentList] = useState([
    { equipment_name: '', make: '', model: '', serial_number: '', location: '', quantity: '', scope_type: 'Comprehensive' }
  ]);
  const [attachAnnexureSeparately, setAttachAnnexureSeparately] = useState(false);

  // Department Head (from Contact Directory)
  const [contacts, setContacts] = useState([]);
  const [departmentHeadId, setDepartmentHeadId] = useState('');

  // Schedule of Rates
  const [manpowerRates, setManpowerRates] = useState([]);
  const [rateCardItems, setRateCardItems] = useState([]);
  const [otherCharges, setOtherCharges] = useState([]);
  const [uploadingRates, setUploadingRates] = useState(false);

  // Annexure files
  const [annexures, setAnnexures] = useState([]);
  const [uploadingAnnexure, setUploadingAnnexure] = useState(false);

  // General Terms clause selection
  const [templateClauses, setTemplateClauses] = useState([]);
  const [excludedClauses, setExcludedClauses] = useState([]);

  const addEquipment = () => {
    setEquipmentList([...equipmentList, { equipment_name: '', make: '', model: '', serial_number: '', location: '', quantity: '', scope_type: 'Comprehensive' }]);
  };

  const updateEquipment = (index, field, value) => {
    const updated = [...equipmentList];
    updated[index] = { ...updated[index], [field]: value };
    setEquipmentList(updated);
  };

  const removeEquipment = (index) => {
    if (equipmentList.length > 1) {
      setEquipmentList(equipmentList.filter((_, i) => i !== index));
    }
  };

  useEffect(() => {
    axios.get(`${API}/financial-year`).then(res => setCurrentFY(res.data.fy));
    axios.get(`${API}/contacts`).then(res => setContacts(res.data)).catch(() => {});
    axios.get(`${API}/templates/unified`).then(res => setTemplateClauses(res.data?.clauses || [])).catch(() => {});
    
    // Load existing contract data for edit mode
    if (isEdit) {
      axios.get(`${API}/contracts/${editId}`).then(res => {
        const c = res.data;
        setOriginalContract(c);
        const terms = c.amc_terms || c.manpower_terms || {};
        setVendor(c.agreement_header?.second_party || {});
        setAgreementDate(c.agreement_header?.agreement_date || '');
        setStartDate(terms.start_date || '');
        setEndDate(terms.end_date || '');
        setMonthlyValue(terms.monthly_value || 0);
        setGstRate(terms.gst_rate || 18);
        setBillingFrequency(terms.billing_frequency || 'monthly');
        setStatus(c.status || 'draft');
        setScopeDescription(terms.scope_description || terms.description_of_services || '');
        setSpecialConditions(terms.special_conditions || '');
        setVendorConditions(terms.vendor_conditions || '');
        setFacilitiesProvided(terms.facilities_provided || '');
        // Map legacy values to new 3-option scheme
        const rawType = terms.amc_type || 'Work Order';
        const typeMap = { 'Comprehensive': 'CAMC', 'Non-Comprehensive': 'NCAMC' };
        setAmcType(typeMap[rawType] || rawType);
        setPmFrequency(terms.pm_frequency || '');
        setResponseTime(terms.response_time || '');
        setResolutionTime(terms.resolution_time || '');
        setPmActivities(terms.preventive_maintenance_activities?.length > 0 ? terms.preventive_maintenance_activities : ['']);
        setBreakdownObligations(terms.breakdown_maintenance_obligations || '');
        setDepartmentHeadId(c.department_head_contact_id || '');
        if (terms.equipment_list?.length > 0) {
          setShowEquipment(true);
          setEquipmentList(terms.equipment_list);
        }
        if (terms.pm_frequency || terms.response_time || terms.resolution_time || 
            (terms.preventive_maintenance_activities?.length > 0 && terms.preventive_maintenance_activities.some(a => a)) ||
            terms.breakdown_maintenance_obligations) {
          setShowMaintenance(true);
        }
        setAttachAnnexureSeparately(terms.annexure_attached_separately || false);
        setManpowerRates(terms.manpower_rates || []);
        setRateCardItems(terms.rate_card_items || []);
        setOtherCharges(terms.other_charges || []);
        setExcludedClauses(c.excluded_clauses || []);
        setPreviewNum(c.contract_number);
        // Load annexures
        axios.get(`${API}/contracts/${editId}/annexures`).then(r => setAnnexures(r.data)).catch(() => {});
      }).catch(() => {
        toast.error("Failed to load contract for editing");
        navigate('/contracts');
      }).finally(() => setLoadingEdit(false));
    } else {
      // Check navigation state for AI-extracted data
      const state = window.history.state?.usr;
      if (state?.extractedData) {
        const extracted = state.extractedData;
        if (extracted.agreement_header?.second_party) {
          setVendor(extracted.agreement_header.second_party);
        }
        const terms = extracted.manpower_terms || extracted.amc_terms || {};
        setStartDate(terms.start_date || '');
        setEndDate(terms.end_date || '');
        setMonthlyValue(terms.monthly_value || 0);
        setGstRate(terms.gst_rate || 18);
        setScopeDescription(terms.description_of_services || terms.scope_description || '');
        setSpecialConditions(terms.special_conditions || '');
        setVendorConditions(terms.vendor_conditions || '');
        setFacilitiesProvided(terms.facilities_provided || '');
        setPmFrequency(terms.pm_frequency || '');
        setResponseTime(terms.response_time || '');
        setResolutionTime(terms.resolution_time || '');
      }
    }
  }, []);

  useEffect(() => {
    if (!isEdit) setPreviewNum(`GHL/WO/${currentFY}/XXX`);
  }, [currentFY, isEdit]);

  const annualValue = billingFrequency === 'yearly' ? monthlyValue : billingFrequency === 'halfyearly' ? monthlyValue * 2 : billingFrequency === 'quarterly' ? monthlyValue * 4 : monthlyValue * 12;
  const monthlyGST = monthlyValue * (gstRate / 100);
  const monthlyTotal = monthlyValue + monthlyGST;
  const freqLabel = billingFrequency === 'yearly' ? 'Yearly' : billingFrequency === 'halfyearly' ? 'Half-Yearly' : billingFrequency === 'quarterly' ? 'Quarterly' : 'Monthly';
  const freqMultiplier = billingFrequency === 'yearly' ? 1 : billingFrequency === 'halfyearly' ? 2 : billingFrequency === 'quarterly' ? 4 : 12;

  // Auto-calculate value from schedule of rates
  const manpowerTotal = manpowerRates.reduce((sum, r) => {
    if (r.uom === 'Per Day') return sum + (r.quantity || 0) * (r.rate_per_unit || 0) * (r.estimated_days || 26);
    return sum + (r.quantity || 0) * (r.total_ctc || 0);
  }, 0);
  const otherTotal = otherCharges.reduce((sum, r) => sum + (r.estimated_value || 0), 0);
  const rateCardTotal = rateCardItems.reduce((sum, r) => sum + (r.amount || 0), 0);
  const ratesTotal = amcType === 'Work Order'
    ? manpowerTotal + rateCardTotal + otherTotal
    : rateCardTotal + otherTotal;
  
  useEffect(() => {
    if (ratesTotal > 0) setMonthlyValue(ratesTotal);
  }, [ratesTotal]);

  const buildContractData = () => ({
    contract_type: 'services',
    agreement_header: {
      agreement_date: agreementDate,
      second_party: vendor
    },
    status: status,
    department_head_contact_id: departmentHeadId || null,
    excluded_clauses: excludedClauses,
    amc_terms: {
      scope_description: scopeDescription,
      special_conditions: specialConditions,
      vendor_conditions: vendorConditions,
      facilities_provided: facilitiesProvided,
      amc_type: amcType,
      pm_frequency: pmFrequency,
      response_time: responseTime,
      resolution_time: resolutionTime,
      start_date: startDate,
      end_date: endDate,
      monthly_value: monthlyValue,
      gst_rate: gstRate,
      billing_frequency: billingFrequency,
      preventive_maintenance_activities: pmActivities.filter(a => a.trim()),
      breakdown_maintenance_obligations: breakdownObligations,
      equipment_list: showEquipment && !attachAnnexureSeparately ? equipmentList : [],
      annexure_attached_separately: attachAnnexureSeparately,
      manpower_rates: amcType === 'Work Order' ? manpowerRates.filter(r => r.designation) : [],
      rate_card_items: rateCardItems.filter(r => r.item_name),
      other_charges: otherCharges.filter(r => r.description),
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // For active contracts, show reason modal first
    if (isEdit && originalContract?.status === 'active') {
      setShowReasonModal(true);
      return;
    }
    
    await doSave();
  };

  const doSave = async (reason) => {
    setSaving(true);
    setShowReasonModal(false);
    const contractData = buildContractData();

    try {
      if (isEdit) {
        const reasonParam = reason ? `?reason=${encodeURIComponent(reason)}` : '';
        await axios.put(`${API}/contracts/${editId}${reasonParam}`, contractData);
        toast.success(`Work Order ${previewNum} updated!`);
        navigate(`/contracts/${editId}`);
      } else {
        const response = await axios.post(`${API}/contracts`, contractData);
        toast.success(`Work Order ${response.data.contract_number} created!`);
        navigate(`/contracts/${response.data.id}`);
      }
    } catch (error) {
      toast.error(isEdit ? "Failed to update work order" : "Failed to create work order");
    } finally {
      setSaving(false);
    }
  };

  if (loadingEdit) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--navy)' }} /></div>;
  }

  return (
    <div className="animate-fadeIn" data-testid="create-contract-page">
      <div className="breadcrumb">
        <Link to="/">Dashboard</Link>
        <span>&rsaquo;</span>
        {isEdit ? <><Link to={`/contracts/${editId}`}>{previewNum}</Link><span>&rsaquo;</span><span>Edit</span></> : <span>New Work Order</span>}
      </div>

      {/* Reason Modal for Active Edits - Portal to body */}
      {showReasonModal && ReactDOM.createPortal(
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          data-testid="reason-modal">
          <div style={{ background: '#fff', borderRadius: '8px', padding: '28px', maxWidth: '480px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ fontWeight: 600, fontSize: '16px', color: 'var(--navy)', marginBottom: '4px' }}>Editing Active Work Order</div>
            <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px' }}>
              Changes to an active contract will be automatically logged as an amendment for audit trail.
            </div>
            <label style={{ fontWeight: 500, fontSize: '13px', display: 'block', marginBottom: '6px' }}>Reason for Change *</label>
            <textarea
              value={editReason}
              onChange={e => setEditReason(e.target.value)}
              rows={3}
              placeholder="e.g., Rate revision as per vendor negotiation, Scope extension requested by operations..."
              style={{ width: '100%', padding: '10px', border: '1px solid var(--gray-border)', borderRadius: '6px', fontSize: '13px', resize: 'vertical' }}
              data-testid="edit-reason-input"
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button type="button" className="btn btn-ghost" onClick={() => setShowReasonModal(false)}>Cancel</button>
              <button type="button" className="btn btn-primary" disabled={!editReason.trim()} data-testid="confirm-edit-btn"
                onClick={() => doSave(editReason.trim())}>
                {saving ? 'Saving...' : 'Save & Log Amendment'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <div className="form-card">
        <div style={{ padding: '20px 24px', borderBottom: '2px solid var(--navy)', background: 'var(--gray-light)' }}>
          <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--navy)' }}>
            {isEdit ? `Edit Work Order — ${previewNum}` : 'New Work Order / Service Agreement'}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
            {isEdit && originalContract?.status === 'active' 
              ? 'Editing an active contract — changes will be auto-logged as an amendment.'
              : 'Fill in the details needed for this contract. All sections are optional except vendor details and contract period.'
            }
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-body-inner">
            {/* Contract Identity */}
            <div className="form-section">
              <div className="form-section-title" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span><span className="section-icon"><FileCheck className="w-4 h-4 inline" /></span>Contract Identity</span>
                <TipButton tipId="tip-identity" />
              </div>
              <div className="form-grid three">
                <div className="field">
                  <label>Work Order Number</label>
                  <input className="readonly-field" value={previewNum} readOnly />
                  <div className="field-hint">Auto-assigned on save</div>
                </div>
                <div className="field">
                  <label>Date of Agreement <span className="req">*</span></label>
                  <input type="date" value={agreementDate} onChange={e => setAgreementDate(e.target.value)} required data-testid="agreement-date" />
                </div>
                <div className="field">
                  <label>Financial Year</label>
                  <input className="readonly-field" value={`FY ${currentFY}`} readOnly />
                </div>
              </div>
              {/* Department Head Selector */}
              <div style={{ marginTop: '16px' }}>
                <div className="form-grid">
                  <div className="field">
                    <label>Department Head (from Contact Directory)</label>
                    <select value={departmentHeadId} onChange={e => setDepartmentHeadId(e.target.value)} data-testid="department-head-select">
                      <option value="">-- None --</option>
                      {contacts.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.name}{c.role ? ` — ${c.role}` : ''}{c.department ? ` (${c.department})` : ''}
                        </option>
                      ))}
                    </select>
                    <div className="field-hint">Link a contact from the directory as the responsible department head</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Vendor Details */}
            <div className="form-section">
              <div className="form-section-title" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span><span className="section-icon">🏢</span>Service Provider Details</span>
                <TipButton tipId="tip-vendor" />
              </div>
              <div className="form-grid">
                <div className="field">
                  <label>Vendor / Agency Name <span className="req">*</span></label>
                  <input value={vendor.name} onChange={e => setVendor({...vendor, name: e.target.value})} placeholder="Full legal name" required data-testid="vendor-name" />
                </div>
                <div className="field">
                  <label>Entity Type <span className="req">*</span></label>
                  <select value={vendor.type} onChange={e => setVendor({...vendor, type: e.target.value})} required data-testid="vendor-type">
                    <option value="">Select entity type...</option>
                    <option>Private Limited Company</option>
                    <option>Limited Liability Partnership (LLP)</option>
                    <option>Partnership Firm</option>
                    <option>Sole Proprietorship</option>
                    <option>Public Limited Company</option>
                  </select>
                </div>
                <div className="field span2">
                  <label>Registered Address</label>
                  <input value={vendor.registered_office} onChange={e => setVendor({...vendor, registered_office: e.target.value})} placeholder="Full registered address" data-testid="vendor-address" />
                </div>
                <div className="field">
                  <label>Authorized Signatory <span className="req">*</span></label>
                  <input value={vendor.representative_name} onChange={e => setVendor({...vendor, representative_name: e.target.value})} placeholder="Name of signatory" required data-testid="vendor-rep" />
                </div>
                <div className="field">
                  <label>Designation</label>
                  <input value={vendor.representative_designation} onChange={e => setVendor({...vendor, representative_designation: e.target.value})} placeholder="e.g. Managing Director" data-testid="vendor-designation" />
                </div>
                <div className="field">
                  <label>GSTIN</label>
                  <input value={vendor.gstin} onChange={e => {
                    const gstin = e.target.value.toUpperCase();
                    const updates = { gstin };
                    if (gstin.length >= 12) {
                      updates.pan = gstin.substring(2, 12);
                    }
                    setVendor({...vendor, ...updates});
                  }} placeholder="e.g. 27AAXCG1234A1ZA" maxLength={15} data-testid="vendor-gstin" />
                  <div className="field-hint">PAN is auto-extracted from GSTIN</div>
                </div>
                <div className="field">
                  <label>PAN</label>
                  <input value={vendor.pan} onChange={e => setVendor({...vendor, pan: e.target.value.toUpperCase()})} placeholder="Auto-filled from GSTIN" maxLength={10} data-testid="vendor-pan" className={vendor.gstin?.length >= 12 ? 'readonly-field' : ''} readOnly={vendor.gstin?.length >= 12} />
                </div>
              </div>
            </div>

            {/* Period & Financials */}
            <div className="form-section">
              <div className="form-section-title" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span><span className="section-icon">💰</span>Contract Period & Value</span>
                <TipButton tipId="tip-period" />
              </div>
              <div className="form-grid three">
                <div className="field">
                  <label>Start Date <span className="req">*</span></label>
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} required data-testid="start-date" />
                </div>
                <div className="field">
                  <label>End Date <span className="req">*</span></label>
                  <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} required data-testid="end-date" />
                </div>
                <div className="field">
                  <label>Status</label>
                  <select value={status} onChange={e => setStatus(e.target.value)} data-testid="status">
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                  </select>
                </div>
                <div className="field">
                  <label>Billing Frequency</label>
                  <select value={billingFrequency} onChange={e => setBillingFrequency(e.target.value)} data-testid="billing-frequency">
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="halfyearly">Half-Yearly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
                <div className="field">
                  <label>{freqLabel} Value (excl. GST) <span className="req">*</span></label>
                  <input type="number" value={monthlyValue} onChange={e => setMonthlyValue(parseFloat(e.target.value) || 0)} placeholder="e.g. 85000" required data-testid="monthly-value" />
                </div>
                <div className="field">
                  <label>GST Rate</label>
                  <select value={gstRate} onChange={e => setGstRate(parseInt(e.target.value))} data-testid="gst-rate">
                    <option value={18}>18%</option>
                    <option value={12}>12%</option>
                    <option value={5}>5%</option>
                    <option value={0}>Exempt / 0%</option>
                  </select>
                </div>
                <div className="field">
                  <label>Annual Value (excl. GST)</label>
                  <input type="number" value={annualValue} readOnly className="readonly-field" />
                </div>
              </div>
              {monthlyValue > 0 && (
                <div className="gst-preview">
                  <strong>{freqLabel}:</strong> {formatCurrency(monthlyValue)} + GST {formatCurrency(monthlyGST)} = <strong>{formatCurrency(monthlyTotal)}</strong> &nbsp;|&nbsp;
                  <strong>Annual:</strong> {formatCurrency(annualValue)} + GST {formatCurrency(annualValue * gstRate / 100)} = <strong>{formatCurrency(annualValue * (1 + gstRate/100))}</strong>
                </div>
              )}
            </div>

            {/* Scope of Work */}
            <div className="form-section">
              <div className="form-section-title" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span><span className="section-icon"><FileCheck className="w-4 h-4 inline" /></span>Scope of Work</span>
                <TipButton tipId="tip-scope" />
              </div>
              <div className="form-grid">
                <div className="field">
                  <label>Work Order Type</label>
                  <select value={amcType} onChange={e => setAmcType(e.target.value)} data-testid="amc-type">
                    <option value="Work Order">Work Order (General Service / Manpower)</option>
                    <option value="CAMC">CAMC — Comprehensive AMC (Parts + Labour + Repairs)</option>
                    <option value="NCAMC">NCAMC — Non-Comprehensive AMC (Labour Only, Parts Extra)</option>
                  </select>
                  <div className="field-hint">
                    {amcType === 'Work Order' ? 'General service or manpower deployment' : amcType === 'CAMC' ? 'All parts, labour and repairs included in the contract' : 'Service/labour only — spare parts charged separately'}
                  </div>
                </div>
              </div>
              <div className="form-grid full" style={{ marginTop: '12px' }}>
                <div className="field">
                  <label>Description of Services / Scope</label>
                  <textarea value={scopeDescription} onChange={e => setScopeDescription(e.target.value)} rows={3} 
                    placeholder="Describe the scope — e.g., Housekeeping services for all floors, Comprehensive AMC for HVAC systems, Security deployment for main gate..."
                    data-testid="scope-description" />
                </div>
                <div className="field">
                  <label>Conditions to be Satisfied by Vendor</label>
                  <textarea value={vendorConditions} onChange={e => setVendorConditions(e.target.value)} rows={2} 
                    placeholder="PF code, ESI compliance, staffing levels, OEM certifications, experience requirements..." 
                    data-testid="vendor-conditions" />
                </div>
                <div className="field">
                  <label>Facilities Provided by Gnosis</label>
                  <textarea value={facilitiesProvided} onChange={e => setFacilitiesProvided(e.target.value)} rows={2} 
                    placeholder="On-duty meals, uniform washing, storage space, workstations, utilities..." 
                    data-testid="facilities" />
                </div>
                <div className="field">
                  <label>Special Conditions / Exclusions</label>
                  <textarea value={specialConditions} onChange={e => setSpecialConditions(e.target.value)} rows={2} 
                    placeholder="Any specific terms, inclusions, exclusions, certifications, penalty clauses..." 
                    data-testid="special-conditions" />
                </div>
              </div>
            </div>

            {/* Maintenance Details (optional toggle) */}
            <div className="form-section">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: showMaintenance ? '12px' : '0' }}>
                <input type="checkbox" id="showMaintenance" checked={showMaintenance} 
                  onChange={e => setShowMaintenance(e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }} data-testid="show-maintenance-toggle" />
                <label htmlFor="showMaintenance" className="form-section-title" style={{ cursor: 'pointer', margin: 0 }}>
                  <span className="section-icon"><Wrench className="w-4 h-4 inline" /></span>Maintenance Terms & SLA (optional)
                </label>
              </div>
              {showMaintenance && (
                <>
              {/* Upload SLA from file */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: 'var(--navy)', color: '#fff', borderRadius: 'var(--radius)', fontSize: '13px', fontWeight: 500 }}
                  data-testid="upload-maintenance-file">
                  <Upload className="w-4 h-4" />
                  Upload Excel / Word
                  <input type="file" accept=".xlsx,.xls,.docx" style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const res = await axios.post(`${API}/maintenance/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' }});
                        const d = res.data;
                        if (d.pm_frequency) setPmFrequency(d.pm_frequency);
                        if (d.response_time) setResponseTime(d.response_time);
                        if (d.resolution_time) setResolutionTime(d.resolution_time);
                        if (d.pm_activities?.length > 0) setPmActivities(d.pm_activities);
                        if (d.breakdown_obligations) setBreakdownObligations(d.breakdown_obligations);
                        const count = [d.pm_frequency, d.response_time, d.resolution_time].filter(Boolean).length + (d.pm_activities?.length || 0);
                        toast.success(`Extracted ${count} maintenance fields from file`);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Failed to parse file");
                      }
                      e.target.value = '';
                    }}
                  />
                </label>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Upload SLA terms from .xlsx or .docx file</span>
              </div>
              <div className="form-grid">
                <div className="field">
                  <label>PM Frequency</label>
                  <select value={pmFrequency} onChange={e => setPmFrequency(e.target.value)} data-testid="pm-frequency">
                    <option value="">Not applicable</option>
                    <option>Monthly</option>
                    <option>Bi-Monthly</option>
                    <option>Quarterly</option>
                    <option>Half-Yearly</option>
                    <option>Annually</option>
                    <option>As per OEM schedule</option>
                  </select>
                </div>
                <div className="field">
                  <label>Breakdown Response Time</label>
                  <select value={responseTime} onChange={e => setResponseTime(e.target.value)} data-testid="response-time">
                    <option value="">Not applicable</option>
                    <option>Within 2 hours</option>
                    <option>Within 4 hours</option>
                    <option>Within 8 hours</option>
                    <option>Within 24 hours</option>
                    <option>Next business day</option>
                  </select>
                </div>
                <div className="field">
                  <label>Resolution Time</label>
                  <select value={resolutionTime} onChange={e => setResolutionTime(e.target.value)} data-testid="resolution-time">
                    <option value="">Not applicable</option>
                    <option>Within 4 hours</option>
                    <option>Within 8 hours</option>
                    <option>Within 24 hours</option>
                    <option>Within 48 hours</option>
                    <option>Within 72 hours</option>
                  </select>
                </div>
              </div>
              <div className="field" style={{ marginTop: '16px' }}>
                <label>Preventive Maintenance Activities</label>
                {pmActivities.map((activity, index) => (
                  <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'center' }}>
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: '12px', color: 'var(--text-light)', width: '24px', textAlign: 'right' }}>{index + 1}.</span>
                    <input value={activity} 
                      onChange={e => { const u = [...pmActivities]; u[index] = e.target.value; setPmActivities(u); }}
                      placeholder="e.g. Filter cleaning, Oil change, Belt inspection"
                      style={{ flex: 1 }} data-testid={`pm-activity-${index}`} />
                    {pmActivities.length > 1 && (
                      <button type="button" className="icon-btn" onClick={() => setPmActivities(pmActivities.filter((_, i) => i !== index))} style={{ color: 'var(--red)' }}>
                        <span style={{ fontSize: '16px' }}>&times;</span>
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className="btn btn-ghost" onClick={() => setPmActivities([...pmActivities, ''])}
                  style={{ fontSize: '12px', padding: '4px 12px', marginTop: '4px' }} data-testid="add-pm-activity">
                  + Add PM Activity
                </button>
              </div>
              <div className="field" style={{ marginTop: '16px' }}>
                <label>Breakdown / Corrective Obligations</label>
                <textarea value={breakdownObligations} onChange={e => setBreakdownObligations(e.target.value)} rows={2} 
                  placeholder="24/7 helpdesk, site visit within X hours, temporary standby arrangement..."
                  data-testid="breakdown-obligations" />
              </div>
                </>
              )}
            </div>

            {/* Schedule of Rates */}
            <div className="form-section">
              <div className="form-section-title" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span><span className="section-icon"><DollarSign className="w-4 h-4 inline" /></span>Schedule of Rates / Detailed Pricing</span>
                <TipButton tipId="tip-rates" />
              </div>
              
              {/* Upload controls — now handled by Attach Annexure below */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                <button type="button" className="btn btn-ghost" style={{ fontSize: '12px', padding: '8px 12px' }}
                  onClick={() => window.open(`${API}/rates/excel-template/${amcType === 'Work Order' ? 'manpower' : 'ratecard'}`, '_blank')}
                  data-testid="download-rates-template">
                  <Download className="w-3 h-3" style={{ marginRight: '4px' }} /> Download Template
                </button>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                  {amcType === 'Work Order' ? 'Manpower wages format' : 'Rate card format'} — {amcType === 'Work Order' ? (manpowerRates.length || 0) : (rateCardItems.length || 0)} items  |  Upload via "Attach Annexure" below
                </span>
              </div>

              {/* Manpower Rates Table (for Work Order type) */}
              {amcType === 'Work Order' && (
                <>
                  {manpowerRates.length > 0 && (
                    <div style={{ border: '1px solid var(--gray-border)', borderRadius: 'var(--radius)', overflow: 'auto', marginBottom: '12px' }}>
                      <table className="contract-table" style={{ marginBottom: 0, fontSize: '12px' }}>
                        <thead>
                          <tr>
                            <th>#</th><th>Designation / Service</th><th>Count</th><th>UOM</th><th style={{ textAlign: 'right' }}>Rate</th><th>Est. Days</th><th style={{ textAlign: 'right' }}>Estimated Value</th><th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {manpowerRates.map((r, i) => {
                            const estVal = r.uom === 'Per Day' ? (r.quantity || 0) * (r.rate_per_unit || 0) * (r.estimated_days || 26) : (r.quantity || 0) * (r.total_ctc || 0);
                            return (
                              <tr key={i} style={{ cursor: 'default' }}>
                                <td>{i + 1}</td>
                                <td><input value={r.designation} onChange={e => { const u = [...manpowerRates]; u[i] = {...u[i], designation: e.target.value}; setManpowerRates(u); }} style={{ width: '160px', border: 'none', background: 'transparent' }} placeholder="e.g. Housekeeping Staff" /></td>
                                <td><input type="number" value={r.quantity} onChange={e => { const u = [...manpowerRates]; u[i] = {...u[i], quantity: parseInt(e.target.value) || 0}; setManpowerRates(u); }} style={{ width: '50px', border: 'none', background: 'transparent' }} /></td>
                                <td>
                                  <select value={r.uom || 'Monthly'} onChange={e => { const u = [...manpowerRates]; u[i] = {...u[i], uom: e.target.value}; setManpowerRates(u); }} style={{ border: 'none', background: 'transparent', fontSize: '12px' }}>
                                    <option value="Monthly">Monthly</option>
                                    <option value="Per Day">Per Day</option>
                                    <option value="Per Hour">Per Hour</option>
                                  </select>
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {r.uom === 'Per Day' || r.uom === 'Per Hour' ? (
                                    <input type="number" value={r.rate_per_unit || 0} onChange={e => { const u = [...manpowerRates]; u[i] = {...u[i], rate_per_unit: parseFloat(e.target.value) || 0}; setManpowerRates(u); }} style={{ width: '80px', border: 'none', background: 'transparent', textAlign: 'right' }} />
                                  ) : (
                                    <input type="number" value={r.total_ctc || 0} onChange={e => { const u = [...manpowerRates]; u[i] = {...u[i], total_ctc: parseFloat(e.target.value) || 0}; setManpowerRates(u); }} style={{ width: '80px', border: 'none', background: 'transparent', textAlign: 'right' }} />
                                  )}
                                </td>
                                <td>
                                  {(r.uom === 'Per Day' || r.uom === 'Per Hour') ? (
                                    <input type="number" value={r.estimated_days || 26} onChange={e => { const u = [...manpowerRates]; u[i] = {...u[i], estimated_days: parseInt(e.target.value) || 0}; setManpowerRates(u); }} style={{ width: '50px', border: 'none', background: 'transparent' }} />
                                  ) : <span style={{ color: 'var(--text-light)', fontSize: '11px' }}>—</span>}
                                </td>
                                <td style={{ fontFamily: "'DM Mono', monospace", textAlign: 'right', fontWeight: 600 }}>{formatCurrency(estVal)}</td>
                                <td><button type="button" className="icon-btn" onClick={() => setManpowerRates(manpowerRates.filter((_, idx) => idx !== i))} style={{ color: 'var(--red)' }}><Trash2 className="w-3 h-3" /></button></td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: 'var(--gray-light)', fontWeight: 600 }}>
                            <td></td><td>TOTAL {freqLabel.toUpperCase()}</td>
                            <td>{manpowerRates.reduce((s, r) => s + (r.quantity || 0), 0)}</td>
                            <td colSpan={3}></td>
                            <td style={{ textAlign: 'right', fontFamily: "'DM Mono', monospace" }}>{formatCurrency(manpowerTotal)}</td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                  <button type="button" className="btn btn-ghost" style={{ fontSize: '12px' }}
                    onClick={() => setManpowerRates([...manpowerRates, { designation: '', quantity: 1, total_ctc: 0, uom: 'Monthly', rate_per_unit: 0, estimated_days: 26 }])}
                    data-testid="add-manpower-rate">
                    + Add Manpower / Service Item
                  </button>
                </>
              )}

              {/* Rate Card Table — available for all contract types (e.g. RK Corp truck+driver rates) */}
              <>
                <div style={{ marginBottom: '6px', fontSize: '12px', color: 'var(--gray-muted)', fontWeight: 500 }}>
                  Rate Card / Schedule of Rates — Reference pricing from vendor (e.g., Truck with Driver, Crane, Hydra, etc.)
                </div>
                {rateCardItems.length > 0 && (
                    <div style={{ border: '1px solid var(--gray-border)', borderRadius: 'var(--radius)', overflow: 'auto', marginBottom: '12px' }}>
                      <table className="contract-table" style={{ marginBottom: 0, fontSize: '13px' }}>
                        <thead>
                          <tr>
                            <th>#</th><th>Item / Service</th><th>Description</th><th>Unit</th><th style={{ textAlign: 'right' }}>Rate</th><th>Qty</th><th style={{ textAlign: 'right' }}>Amount</th><th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {rateCardItems.map((r, i) => (
                            <tr key={i} style={{ cursor: 'default' }}>
                              <td>{i + 1}</td>
                              <td><input value={r.item_name} onChange={e => { const u = [...rateCardItems]; u[i] = {...u[i], item_name: e.target.value}; setRateCardItems(u); }} style={{ width: '140px', border: 'none', background: 'transparent' }} /></td>
                              <td><input value={r.description} onChange={e => { const u = [...rateCardItems]; u[i] = {...u[i], description: e.target.value}; setRateCardItems(u); }} style={{ width: '160px', border: 'none', background: 'transparent' }} /></td>
                              <td><input value={r.unit} onChange={e => { const u = [...rateCardItems]; u[i] = {...u[i], unit: e.target.value}; setRateCardItems(u); }} style={{ width: '70px', border: 'none', background: 'transparent' }} /></td>
                              <td style={{ fontFamily: "'DM Mono', monospace", textAlign: 'right' }}><input type="number" value={r.rate} onChange={e => { const u = [...rateCardItems]; const rate = parseFloat(e.target.value) || 0; u[i] = {...u[i], rate, amount: rate * (u[i].quantity || 0)}; setRateCardItems(u); }} style={{ width: '80px', border: 'none', background: 'transparent', textAlign: 'right' }} /></td>
                              <td><input type="number" value={r.quantity} onChange={e => { const u = [...rateCardItems]; const qty = parseFloat(e.target.value) || 0; u[i] = {...u[i], quantity: qty, amount: (u[i].rate || 0) * qty}; setRateCardItems(u); }} style={{ width: '50px', border: 'none', background: 'transparent' }} /></td>
                              <td style={{ fontFamily: "'DM Mono', monospace", textAlign: 'right', fontWeight: 600 }}>{formatCurrency(r.amount)}</td>
                              <td><button type="button" className="icon-btn" onClick={() => setRateCardItems(rateCardItems.filter((_, idx) => idx !== i))} style={{ color: 'var(--red)' }}><Trash2 className="w-3 h-3" /></button></td>
                            </tr>
                          ))}
                        </tbody>
                        {rateCardItems.length > 0 && (
                          <tfoot>
                            <tr style={{ background: 'var(--gray-light)', fontWeight: 600 }}>
                              <td></td><td colSpan={5}>TOTAL</td>
                              <td style={{ textAlign: 'right', fontFamily: "'DM Mono', monospace" }}>{formatCurrency(ratesTotal)}</td>
                              <td></td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  )}
                  <button type="button" className="btn btn-ghost" style={{ fontSize: '12px' }}
                    onClick={() => setRateCardItems([...rateCardItems, { item_name: '', description: '', unit: '', rate: 0, quantity: 0, amount: 0 }])}
                    data-testid="add-rate-card-item">
                    + Add Rate Card Item
                  </button>
                </>

              {ratesTotal > 0 && (
                <div className="gst-preview" style={{ marginTop: '12px' }}>
                  Calculated from rates: <strong>{formatCurrency(ratesTotal)}</strong> /{freqLabel.toLowerCase()} (auto-fills {freqLabel} Value above)
                </div>
              )}

              {/* Other Charges (billed on actuals) */}
              <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px dashed var(--gray-border)' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--navy)', marginBottom: '10px' }}>
                  Other Charges (Estimated / Billed on Actuals)
                </div>
                {otherCharges.length > 0 && (
                  <div style={{ border: '1px solid var(--gray-border)', borderRadius: 'var(--radius)', overflow: 'auto', marginBottom: '12px' }}>
                    <table className="contract-table" style={{ marginBottom: 0, fontSize: '12px' }}>
                      <thead>
                        <tr>
                          <th>#</th><th>Description</th><th>UOM</th><th style={{ textAlign: 'right' }}>Rate</th><th>Est. Qty</th><th style={{ textAlign: 'right' }}>Estimated Value</th><th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {otherCharges.map((r, i) => (
                          <tr key={i} style={{ cursor: 'default' }}>
                            <td>{i + 1}</td>
                            <td><input value={r.description} onChange={e => { const u = [...otherCharges]; u[i] = {...u[i], description: e.target.value}; setOtherCharges(u); }} style={{ width: '180px', border: 'none', background: 'transparent' }} placeholder="e.g. Kitchen waste disposal" /></td>
                            <td>
                              <select value={r.uom || 'Lump Sum'} onChange={e => { const u = [...otherCharges]; u[i] = {...u[i], uom: e.target.value}; setOtherCharges(u); }} style={{ border: 'none', background: 'transparent', fontSize: '12px' }}>
                                <option value="Lump Sum">Lump Sum</option>
                                <option value="Per Trip">Per Trip</option>
                                <option value="Per Unit">Per Unit</option>
                                <option value="Per KG">Per KG</option>
                                <option value="Per Visit">Per Visit</option>
                                <option value="Per Month">Per Month</option>
                              </select>
                            </td>
                            <td style={{ textAlign: 'right' }}><input type="number" value={r.rate || 0} onChange={e => { const u = [...otherCharges]; const rate = parseFloat(e.target.value) || 0; u[i] = {...u[i], rate, estimated_value: rate * (u[i].estimated_qty || 0)}; setOtherCharges(u); }} style={{ width: '80px', border: 'none', background: 'transparent', textAlign: 'right' }} /></td>
                            <td><input type="number" value={r.estimated_qty || 0} onChange={e => { const u = [...otherCharges]; const qty = parseFloat(e.target.value) || 0; u[i] = {...u[i], estimated_qty: qty, estimated_value: (u[i].rate || 0) * qty}; setOtherCharges(u); }} style={{ width: '60px', border: 'none', background: 'transparent' }} /></td>
                            <td style={{ fontFamily: "'DM Mono', monospace", textAlign: 'right', fontWeight: 600 }}>{formatCurrency(r.estimated_value || 0)}</td>
                            <td><button type="button" className="icon-btn" onClick={() => setOtherCharges(otherCharges.filter((_, idx) => idx !== i))} style={{ color: 'var(--red)' }}><Trash2 className="w-3 h-3" /></button></td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: 'var(--gold-pale)', fontWeight: 600 }}>
                          <td></td><td colSpan={4}>TOTAL ESTIMATED (ON ACTUALS)</td>
                          <td style={{ textAlign: 'right', fontFamily: "'DM Mono', monospace" }}>{formatCurrency(otherTotal)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
                <button type="button" className="btn btn-ghost" style={{ fontSize: '12px' }}
                  onClick={() => setOtherCharges([...otherCharges, { description: '', uom: 'Lump Sum', rate: 0, estimated_qty: 0, estimated_value: 0 }])}
                  data-testid="add-other-charge">
                  + Add Other Charge
                </button>
              </div>

              {/* Annexure File Upload — attaches file AND parses data into rates table */}
              <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--gray-border)' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--navy)', marginBottom: '10px' }}>
                  <FileText className="w-4 h-4 inline" style={{ marginRight: '6px' }} />
                  Attach Annexure Files
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
                  <label style={{ cursor: uploadingAnnexure ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: 'var(--navy)', color: '#fff', borderRadius: 'var(--radius)', fontSize: '13px', fontWeight: 500, opacity: uploadingAnnexure ? 0.7 : 1 }}
                    data-testid="upload-annexure-file">
                    <Upload className="w-4 h-4" />
                    {uploadingAnnexure ? 'Processing...' : 'Upload File'}
                    <input type="file" accept=".xlsx,.xls,.docx,.doc,.pdf,.csv" style={{ display: 'none' }}
                      disabled={uploadingAnnexure}
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        setUploadingAnnexure(true);
                        const fname = file.name.toLowerCase();
                        const isParseableFile = fname.endsWith('.xlsx') || fname.endsWith('.xls') || fname.endsWith('.docx');
                        
                        // Step 1: Try to parse rates data from the file
                        if (isParseableFile) {
                          try {
                            const rateType = amcType === 'Work Order' ? 'manpower' : 'ratecard';
                            const parseForm = new FormData();
                            parseForm.append('file', file);
                            const parseRes = await axios.post(`${API}/rates/upload-excel?rate_type=${rateType}`, parseForm, { headers: { 'Content-Type': 'multipart/form-data' }});
                            if (rateType === 'manpower' && parseRes.data.manpower_rates?.length > 0) {
                              setManpowerRates(parseRes.data.manpower_rates);
                              toast.success(`Loaded ${parseRes.data.count} manpower rate items from file`);
                            } else if (rateType === 'ratecard' && parseRes.data.rate_card_items?.length > 0) {
                              setRateCardItems(parseRes.data.rate_card_items);
                              toast.success(`Loaded ${parseRes.data.count} rate card items from file`);
                            }
                          } catch (err) { console.warn('No parseable rate data in file; attaching only', err); }
                        }
                        
                        // Step 2: Attach file as annexure (if editing existing contract)
                        if (isEdit) {
                          try {
                            const attachForm = new FormData();
                            attachForm.append('file', file);
                            const res = await axios.post(`${API}/contracts/${editId}/annexures?label=${encodeURIComponent(file.name)}`, attachForm, { headers: { 'Content-Type': 'multipart/form-data' }});
                            setAnnexures(prev => [...prev, res.data]);
                            toast.success(`Annexure "${file.name}" attached`);
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Failed to attach annexure");
                          }
                        } else {
                          if (isParseableFile) {
                            toast.info('Data extracted from file. Save the work order first, then the file will be available as an annexure.');
                          }
                        }
                        
                        setUploadingAnnexure(false);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    Upload Excel or Word — data is extracted into the table AND file is attached as annexure
                  </span>
                </div>
                {!isEdit && annexures.length === 0 && (
                  <div style={{ padding: '10px 14px', background: 'var(--gold-pale)', border: '1px solid var(--gold)', borderRadius: 'var(--radius)', fontSize: '12px', color: 'var(--amber)' }}>
                    Data will be extracted from uploaded files immediately. File attachment as annexure will happen after saving the work order.
                  </div>
                )}
                {annexures.length > 0 && (
                  <div style={{ border: '1px solid var(--gray-border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                    <table className="contract-table" style={{ marginBottom: 0, fontSize: '13px' }}>
                      <thead>
                        <tr>
                          <th>#</th><th>Document</th><th>Type</th><th>Size</th><th>Uploaded</th><th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {annexures.map((a, i) => (
                          <tr key={a.id} style={{ cursor: 'default' }}>
                            <td style={{ fontFamily: "'DM Mono', monospace" }}>{a.annexure_number || i + 1}</td>
                            <td style={{ fontWeight: 500 }}>
                              <a href={`${API}/annexures/${a.id}/download`} target="_blank" rel="noreferrer" style={{ color: 'var(--navy)', textDecoration: 'underline' }}>
                                {a.label || a.original_filename}
                              </a>
                            </td>
                            <td><span className="badge badge-draft">{(a.file_type || '').toUpperCase()}</span></td>
                            <td style={{ fontFamily: "'DM Mono', monospace", fontSize: '11px' }}>{a.file_size ? `${(a.file_size / 1024).toFixed(1)} KB` : '-'}</td>
                            <td style={{ fontFamily: "'DM Mono', monospace", fontSize: '11px' }}>{(a.created_at || '').substring(0, 10)}</td>
                            <td>
                              <button type="button" className="icon-btn" style={{ color: 'var(--red)' }}
                                onClick={async () => {
                                  if (!window.confirm('Delete this annexure?')) return;
                                  try {
                                    await axios.delete(`${API}/annexures/${a.id}`);
                                    setAnnexures(prev => prev.filter(x => x.id !== a.id));
                                    toast.success('Annexure deleted');
                                  } catch { toast.error('Failed to delete'); }
                                }}>
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Equipment (optional toggle) */}
            <div className="form-section">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <input type="checkbox" id="showEquipment" checked={showEquipment} 
                  onChange={e => setShowEquipment(e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }} data-testid="show-equipment-toggle" />
                <label htmlFor="showEquipment" className="form-section-title" style={{ cursor: 'pointer', margin: 0 }}>
                  <span className="section-icon">📎</span>Equipment Schedule (Annexure)
                </label>
              </div>
              
              {showEquipment && (
                <>
                  <div style={{ background: 'var(--gold-pale)', border: '1px solid var(--gold)', borderRadius: 'var(--radius)', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <input type="checkbox" id="attachSep" checked={attachAnnexureSeparately}
                      onChange={e => setAttachAnnexureSeparately(e.target.checked)}
                      style={{ width: '16px', height: '16px' }} data-testid="attach-separately" />
                    <label htmlFor="attachSep" style={{ cursor: 'pointer', fontSize: '13px' }}>
                      <strong>Attach separately</strong> — Equipment list will be referenced but not included inline
                    </label>
                  </div>

                  {/* Excel Upload for Equipment */}
                  {!attachAnnexureSeparately && (
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <label style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: 'var(--navy)', color: '#fff', borderRadius: 'var(--radius)', fontSize: '13px', fontWeight: 500 }}
                        data-testid="upload-equipment-excel">
                        <Upload className="w-4 h-4" />
                        Upload from Excel
                        <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            const formData = new FormData();
                            formData.append('file', file);
                            try {
                              const res = await axios.post(`${API}/equipment/upload-excel`, formData, { headers: { 'Content-Type': 'multipart/form-data' }});
                              if (res.data.equipment?.length > 0) {
                                setEquipmentList(res.data.equipment);
                                toast.success(`Loaded ${res.data.count} equipment items from Excel`);
                              } else {
                                toast.warning("No equipment data found in the file");
                              }
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Failed to parse Excel file");
                            }
                            e.target.value = '';
                          }}
                        />
                      </label>
                      <button type="button" className="btn btn-ghost" style={{ fontSize: '12px', padding: '8px 12px' }}
                        onClick={() => window.open(`${API}/equipment/excel-template`, '_blank')}
                        data-testid="download-equipment-template">
                        <Download className="w-3 h-3" style={{ marginRight: '4px' }} /> Download Template
                      </button>
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        {equipmentList.length > 0 && equipmentList[0].equipment_name ? `${equipmentList.length} items loaded` : 'Upload .xlsx with equipment data'}
                      </span>
                    </div>
                  )}

                  {!attachAnnexureSeparately && equipmentList.map((eq, index) => (
                    <div key={index} style={{ padding: '14px', border: '1px solid var(--gray-border)', borderRadius: 'var(--radius)', marginBottom: '10px', background: index % 2 === 0 ? '#fafbfc' : '#fff' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <span style={{ fontWeight: 500, fontSize: '13px', color: 'var(--navy)' }}>Equipment #{index + 1}</span>
                        {equipmentList.length > 1 && (
                          <button type="button" className="icon-btn" onClick={() => removeEquipment(index)} style={{ color: 'var(--red)' }}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <div className="form-grid three">
                        <div className="field"><label>Equipment</label><input value={eq.equipment_name} onChange={e => updateEquipment(index, 'equipment_name', e.target.value)} placeholder="e.g. Split AC, DG Set" data-testid={`eq-name-${index}`} /></div>
                        <div className="field"><label>Make</label><input value={eq.make} onChange={e => updateEquipment(index, 'make', e.target.value)} placeholder="Brand" /></div>
                        <div className="field"><label>Model</label><input value={eq.model} onChange={e => updateEquipment(index, 'model', e.target.value)} placeholder="Model" /></div>
                        <div className="field"><label>Serial No.</label><input value={eq.serial_number} onChange={e => updateEquipment(index, 'serial_number', e.target.value)} placeholder="S/N" /></div>
                        <div className="field"><label>Location</label><input value={eq.location} onChange={e => updateEquipment(index, 'location', e.target.value)} placeholder="Area" /></div>
                        <div className="field"><label>Qty</label><input value={eq.quantity} onChange={e => updateEquipment(index, 'quantity', e.target.value)} placeholder="Nos" /></div>
                      </div>
                    </div>
                  ))}
                  {!attachAnnexureSeparately && (
                    <button type="button" className="btn btn-ghost" onClick={addEquipment} style={{ marginTop: '4px' }} data-testid="add-equipment">
                      + Add Equipment
                    </button>
                  )}
                </>
              )}
            </div>
            {/* General Terms Clause Selection */}
            {templateClauses.length > 0 && (
              <div className="form-section">
                <div className="form-section-title"><span className="section-icon"><FileCheck className="w-4 h-4 inline" /></span>General Terms — Select Clauses for this Contract</div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>
                  Uncheck clauses you want to exclude from this specific work order. Excluded clauses won't appear in Word/PDF exports. The master template stays unchanged.
                </div>
                <div style={{ display: 'grid', gap: '6px' }}>
                  {templateClauses.map((clause) => {
                    const isExcluded = excludedClauses.includes(clause.number);
                    return (
                      <div key={clause.number} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '8px 12px', background: isExcluded ? 'var(--gold-pale)' : 'var(--gray-light)', borderRadius: 'var(--radius)', border: isExcluded ? '1px solid var(--gold)' : '1px solid transparent', opacity: isExcluded ? 0.6 : 1 }}>
                        <input type="checkbox" checked={!isExcluded}
                          onChange={() => {
                            if (isExcluded) {
                              setExcludedClauses(excludedClauses.filter(n => n !== clause.number));
                            } else {
                              setExcludedClauses([...excludedClauses, clause.number]);
                            }
                          }}
                          style={{ width: '16px', height: '16px', marginTop: '2px', cursor: 'pointer', flexShrink: 0 }}
                          data-testid={`clause-toggle-${clause.number}`}
                        />
                        <div>
                          <div style={{ fontWeight: 500, fontSize: '13px', textDecoration: isExcluded ? 'line-through' : 'none' }}>
                            {clause.number}. {clause.title}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {excludedClauses.length > 0 && (
                  <div style={{ marginTop: '10px', fontSize: '12px', color: 'var(--amber)', fontWeight: 500 }}>
                    {excludedClauses.length} clause{excludedClauses.length > 1 ? 's' : ''} excluded from this work order
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-footer">
            <button type="button" className="btn btn-ghost" onClick={() => isEdit ? navigate(`/contracts/${editId}`) : navigate('/')}>Cancel</button>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span style={{ fontFamily: "'DM Mono', monospace", fontSize: '12px', color: 'var(--navy)', opacity: 0.7 }}>{previewNum}</span>
              <button type="submit" className="btn btn-primary" disabled={saving} data-testid="submit-contract">
                {saving ? 'Saving...' : isEdit ? 'Update Work Order' : 'Create Work Order'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateContract;
